/**
 * Date helpers. The API sends DueDate / CloseDate / StageSince as ISO strings that are
 * calendar dates; they must be read as LOCAL dates so "2026-10-12T00:00:00Z" never
 * shows as the 11th west of UTC.
 */

/**
 * @param {string|Date|null|undefined} value
 * @returns {Date|null} local midnight for date-only input, or the exact instant for a Date
 */
export const parseLocalDate = value => {
    if (!value) return null
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
    if (!match) return null
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    return Number.isNaN(date.getTime()) ? null : date
}

const startOfDay = date => new Date(date.getFullYear(), date.getMonth(), date.getDate())

/**
 * Whole calendar days from `from` (default today) to `value`. Negative = in the past.
 * @param {string|Date|null|undefined} value
 * @param {Date} [from]
 * @returns {number|null}
 */
export const daysFrom = (value, from = new Date()) => {
    const date = parseLocalDate(value)
    if (!date) return null
    return Math.round((startOfDay(date).getTime() - startOfDay(from).getTime()) / 86400000)
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * "Sep 18" (same year) or "Sep 18, 2025".
 * @param {string|Date|null|undefined} value
 * @param {Date} [now]
 * @returns {string}
 */
export const formatShortDate = (value, now = new Date()) => {
    const date = parseLocalDate(value)
    if (!date) return ''
    const base = `${MONTHS[date.getMonth()]} ${date.getDate()}`
    return date.getFullYear() === now.getFullYear() ? base : `${base}, ${date.getFullYear()}`
}

/**
 * "today", "yesterday", "21 days ago", "tomorrow", "in 5 days".
 * @param {string|Date|null|undefined} value
 * @param {Date} [now]
 * @returns {string}
 */
export const relativeDays = (value, now = new Date()) => {
    const diff = daysFrom(value, now)
    if (diff === null) return ''
    if (diff === 0) return 'today'
    if (diff === -1) return 'yesterday'
    if (diff === 1) return 'tomorrow'
    return diff < 0 ? `${-diff} days ago` : `in ${diff} days`
}

/**
 * "Sep 18 · 21 days ago", or "Never" when empty.
 * @param {string|Date|null|undefined} value
 * @param {Date} [now]
 */
export const lastContactedLabel = (value, now = new Date()) =>
    value ? `${formatShortDate(value, now)} · ${relativeDays(value, now)}` : 'Never'

/** @param {number|null|undefined} amount */
export const formatMoney = amount => {
    const n = Number(amount || 0)
    return `$${Math.round(n).toLocaleString('en-US')}`
}

/** "$166k" style for KPI tiles. @param {number|null|undefined} amount */
export const formatMoneyShort = amount => {
    const n = Number(amount || 0)
    if (Math.abs(n) >= 1000000) return `$${(n / 1000000).toFixed(1).replace(/\.0$/, '')}M`
    if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(n >= 100000 ? 0 : 1).replace(/\.0$/, '')}k`
    return `$${Math.round(n)}`
}

/** Local yyyy-MM-dd for <input type="date"> and API date fields. @param {Date} date */
export const toDateInput = date => {
    const pad = n => String(n).padStart(2, '0')
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}
